import {List, Map as ImmutableMap} from 'immutable';
import alt from 'components/Dispatcher';
import AdminFilterWordProfileActions from 'actions/admin/AdminFilterWordProfileActions';
import ToastStore from '@store/toast/ToastStore';
import NewFilterWordProfilesInAccount from 'records/admin/topics/NewFilterWordProfilesInAccount';
import NewFilterWordProfile, {FilterWordProfileId} from 'records/admin/topics/NewFilterWordProfile';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import {Store} from '@store/@types/alt';
import DepartmentWithTeams from 'records/admin/topics/DepartmentWithTeams';
import TriggerRuleInfo from 'records/TriggerRuleInfo';
import Team from 'records/Team';
import {DepartmentId} from 'records/Department';
import {TeamId} from 'records/TeamId';

/**
 * Each instance represents a usage of a profile. These come in two varieties:
 *
 * 1. department is set, team is absent: represents that department's profile.
 * 2. department and team are set: represents that team's profile. The department is the team's department.
 */
interface FilterWordProfileUsageRecord {
  department: DepartmentWithTeams;
  team?: Team;
}

interface AdminFilterWordProfileStoreState {
  profilesInAccount: NewFilterWordProfilesInAccount;
  profilesInAccountLoading: boolean;
  profilesInAccountError: boolean;
  idToProfileMap: ImmutableMap<FilterWordProfileId, NewFilterWordProfile>;
  topicsInAccount: List<NewFilterWord>;
  profileDepartmentAndTeamUsage: ImmutableMap<
    FilterWordProfileId,
    List<FilterWordProfileUsageRecord>
  >;
  wordsToTopics: ImmutableMap<string, List<NewFilterWord>>;
  departments: List<DepartmentWithTeams>;
  departmentsLoading: boolean;
  departmentsError: boolean;
  triggers: List<TriggerRuleInfo>;
  triggersLoading: boolean;
  triggersError: boolean;
}

const EMPTY_PROFILES = new NewFilterWordProfilesInAccount();

/**
 * Store for the filter word and topics admin. DO NOT use this on other pages, please.
 */
class AdminFilterWordProfileStore extends Store<AdminFilterWordProfileStoreState> {
  constructor() {
    super();

    this.state = {
      profilesInAccount: EMPTY_PROFILES,
      profilesInAccountLoading: true,
      profilesInAccountError: false,
      idToProfileMap: ImmutableMap(),
      topicsInAccount: List(),
      // Map of filter word ids to a list of objects having a department property and an optional
      // team property, containing the department using the profile or the department containing
      // the team using the profile together with the team.
      profileDepartmentAndTeamUsage: ImmutableMap(),
      // Map of topic words to the topics referencing them (in alphabetical order by topic name).
      wordsToTopics: ImmutableMap(),
      departments: List(),
      departmentsLoading: true,
      departmentsError: false,
      triggers: List(), // triggers that reference a particular topic.
      triggersLoading: true,
      triggersError: false,
    };
    this.bindActions(AdminFilterWordProfileActions);
  }

  associateProfileWithDepartmentSuccess([filterWordProfileId, departmentId]: [
    FilterWordProfileId,
    DepartmentId
  ]) {
    const {departments} = this.state;
    const departmentIndex = departments.findIndex(
      department => department.departmentId === departmentId
    );
    const newDepartments = departments.setIn(
      [departmentIndex, 'filterWordProfileId'],
      filterWordProfileId
    );
    this.setState({
      departments: newDepartments,
      profileDepartmentAndTeamUsage: buildUsages(newDepartments),
    });
  }

  associateProfileWithDepartmentError(e) {
    handleGenericError(e);
  }

  associateProfileWithTeamSuccess([filterWordProfileId, departmentId, teamId]: [
    FilterWordProfileId,
    DepartmentId,
    TeamId
  ]) {
    const {departments} = this.state;
    const departmentIndex = departments.findIndex(
      department => department.departmentId === departmentId
    );
    const teamIndex = departments.get(departmentIndex).teams.findIndex(team => team.id === teamId);
    const newDepartments = departments.setIn(
      [departmentIndex, 'teams', teamIndex, 'filterWordProfileId'],
      filterWordProfileId
    );
    this.setState({
      departments: newDepartments,
      profileDepartmentAndTeamUsage: buildUsages(newDepartments),
    });
  }

  associateProfileWithTeamError(e) {
    handleGenericError(e);
  }

  addNewFilterWord(filterWord: NewFilterWord) {
    // New word is never in a profile.
    const {profilesInAccount} = this.state;
    const newProfilesInAccount = profilesInAccount.addUnassociatedFilterWord(filterWord);
    const newTopicsInAccount = extractTopics(newProfilesInAccount);
    this.setState({
      profilesInAccount: newProfilesInAccount,
      topicsInAccount: newTopicsInAccount,
      wordsToTopics: wordsToTopics(newTopicsInAccount),
    });
  }

  cloneFilterWordSuccess(filterWord: NewFilterWord) {
    this.addNewFilterWord(filterWord);
  }

  cloneFilterWordError(e) {
    handleGenericError(e);
  }

  createFilterWordSuccess([filterWord]: [NewFilterWord]) {
    this.addNewFilterWord(filterWord);
  }

  createFilterWordError(e) {
    handleGenericError(e);
  }

  createProfileSuccess([profile, departmentId, teamId, either]) {
    const {departments, profilesInAccount, idToProfileMap} = this.state;
    const newProfilesInAccount = profilesInAccount.addProfile(profile);
    const newIdToProfileMap = idToProfileMap.set(profile.filterWordProfileId, profile);
    const departmentIndex = departments.findIndex(
      department => department.departmentId === departmentId
    );
    if (either.departmentId) {
      const newDepartments = departments.setIn(
        [departmentIndex, 'filterWordProfileId'],
        profile.filterWordProfileId
      );
      this.setState({
        departments: newDepartments,
        idToProfileMap: newIdToProfileMap,
        profilesInAccount: newProfilesInAccount,
      });
    } else {
      const teamIndex = departments
        .get(departmentIndex)
        .teams.findIndex(team => team.id === teamId);
      const newDepartments = departments.setIn(
        [departmentIndex, 'teams', teamIndex, 'filterWordProfileId'],
        profile.filterWordProfileId
      );
      this.setState({
        departments: newDepartments,
        idToProfileMap: newIdToProfileMap,
        profilesInAccount: newProfilesInAccount,
        profileDepartmentAndTeamUsage: buildUsages(newDepartments),
        // New profile contains no filter words, so no need to update topicsInAccount or
        // wordsToProfile here.
      });
    }
  }

  createProfileError(e) {
    // XXX possible annoyance here: someone else created a profile with the same name as the one
    // you just created after you loaded the page => 500 here. We don't have it for validation, so
    // we can't stop you from trying. Ideally we'd pick that specific error out so you know you
    // need to rename. Maybe even fetch the list of profiles again so we can validate.
    handleGenericError(e);
  }

  deleteFilterWordSuccess() {
    // Topics list is reloaded after delete, so no need to muck with state here.
  }

  deleteFilterWordError(e) {
    // XXX possible annoyance here: someone else deleted it => 404.
    console.error(e);
    ToastStore.addToast({
      type: 'error',
      message: 'There was a problem deleting.',
    });
  }

  fetchAccountProfiles() {
    this.setState({
      profilesInAccountError: false,
      profilesInAccountLoading: true,
      profilesInAccount: EMPTY_PROFILES,
      idToProfileMap: ImmutableMap(),
      topicsInAccount: List(),
      wordsToTopics: ImmutableMap(),
    });
  }

  fetchAccountProfilesSuccess(profilesInAccount: NewFilterWordProfilesInAccount) {
    const idToProfileMap = ImmutableMap(
      profilesInAccount.profiles.map(profile => [profile.filterWordProfileId, profile])
    ) as ImmutableMap<FilterWordProfileId, NewFilterWordProfile>; // annoying but apparently necessary.
    const topicsInAccount = extractTopics(profilesInAccount);
    this.setState({
      profilesInAccountLoading: false,
      profilesInAccountError: false,
      profilesInAccount,
      idToProfileMap,
      topicsInAccount,
      wordsToTopics: wordsToTopics(topicsInAccount),
    });
  }

  fetchAccountProfilesError(e) {
    console.error(e);
    this.setState({
      profilesInAccountLoading: false,
      profilesInAccountError: true,
    });
  }

  fetchDepartments() {
    this.setState({
      departments: List(),
      departmentsLoading: true,
      departmentsError: false,
      profileDepartmentAndTeamUsage: ImmutableMap(),
    });
  }

  fetchDepartmentsSuccess(departments) {
    this.setState({
      departments: departments.sort((a, b) => a.name.localeCompare(b.name)),
      departmentsLoading: false,
      departmentsError: false,
      profileDepartmentAndTeamUsage: buildUsages(departments),
    });
  }

  fetchDepartmentsError(e) {
    console.error(e);
    this.setState({
      departments: List(),
      departmentsLoading: false,
      departmentsError: true,
      profileDepartmentAndTeamUsage: ImmutableMap(),
    });
  }

  fetchTriggers() {
    this.setState({
      triggers: List(),
      triggersError: false,
      triggersLoading: true,
    });
  }

  fetchTriggersSuccess(rules) {
    this.setState({
      triggers: rules,
      triggersError: false,
      triggersLoading: false,
    });
  }

  fetchTriggersError(e) {
    console.error(e);
    this.setState({
      triggers: List(),
      triggersError: true,
      triggersLoading: false,
    });
  }

  mergeTopicsError(e) {
    handleGenericError(e);
  }

  updateFilterWord() {
    this.setState({
      // Since we update the profiles using the response, this makes sense.
      profilesInAccountLoading: true,
    });
  }

  updateFilterWordSuccess(filterWord: NewFilterWord) {
    const {profilesInAccount} = this.state;
    const newProfilesInAccount = profilesInAccount.updateFilterWord(filterWord);
    const newTopicsInAccount = extractTopics(newProfilesInAccount);
    this.setState({
      profilesInAccountLoading: false,
      profilesInAccount: newProfilesInAccount,
      topicsInAccount: newTopicsInAccount,
      wordsToTopics: wordsToTopics(newTopicsInAccount),
    });
  }

  updateFilterWordError(e) {
    handleGenericError(e);
  }

  updateProfile() {
    this.setState({
      profilesInAccountError: false,
      profilesInAccountLoading: true,
    });
  }

  updateProfileSuccess(profile: NewFilterWordProfile) {
    const {profilesInAccount, idToProfileMap} = this.state;
    const newProfilesInAccount = profilesInAccount.updateProfile(profile);
    const newTopicsInAccount = extractTopics(newProfilesInAccount);
    this.setState({
      profilesInAccount: newProfilesInAccount,
      profilesInAccountLoading: false,
      profilesInAccountError: false,
      idToProfileMap: idToProfileMap.set(profile.filterWordProfileId, profile),
      topicsInAccount: newTopicsInAccount,
      wordsToTopics: wordsToTopics(newTopicsInAccount),
    });
  }

  updateProfileError(e) {
    handleGenericError(e);
  }
}

/**
 * Log an error to the console, pop up a toast message for the user.
 * @param e the error.
 */
function handleGenericError(e) {
  console.error(e);
  ToastStore.addToast({
    type: 'error',
    message: 'There was a error communicating with the server.',
  });
}

/**
 * Get all topics in the current user's account, sorted by name.
 *
 * @param profilesInAccount collection of all profiles in the current user's account.
 * @return all topics in the current user's account, sorted by name.
 */
function extractTopics(profilesInAccount: NewFilterWordProfilesInAccount): List<NewFilterWord> {
  return (
    profilesInAccount
      .filterWords()
      .filter(filterWord => filterWord.topic)
      // case-insensitive alphabetical sort.
      .sort((a, b) => a.word.localeCompare(b.word))
  );
}

/**
 * Build a map of words to lists of topics containing those words.
 *
 * @param topicsInAccount list of all topics in the account.
 * @return a map of words to the lists of topics containing those words (sorted by name).
 */
function wordsToTopics(
  topicsInAccount: List<NewFilterWord>
): ImmutableMap<string, List<NewFilterWord>> {
  const result: Map<string, NewFilterWord[]> = new Map();
  topicsInAccount.forEach(topic => {
    topic.alternateSpellings.forEach(alt => {
      const lowercase = alt.toLowerCase();
      const currentList = result.get(lowercase) || [];
      currentList.push(topic);
      result.set(lowercase, currentList);
    });
  });
  // Sort topics alphabetically by word for display purposes (error messages will have the
  // topics in this order).
  const m = ImmutableMap(result) as ImmutableMap<string, List<NewFilterWord>>; // annoying but apparently necessary.
  return m.map(list => list.sort((a, b) => a.word.localeCompare(b.word)));
}

/**
 * Build the department and team usage info.
 * @param departments all departments (with the teams they contain).
 * @return a map of profile ids to lists of usage records, each representing either a department or a team using that
 * profile.
 */
function buildUsages(
  departments: List<DepartmentWithTeams>
): ImmutableMap<FilterWordProfileId, List<FilterWordProfileUsageRecord>> {
  const usages: Record<string, FilterWordProfileUsageRecord[]> = {};
  departments.forEach(department => maybeAddDepartmentUsage(usages, department));
  // In the object we've built, the keys are strings, but profile ids are actually numbers. We want the numbers back.
  const entries = List(Object.entries(usages)).map(([k, v]) => [Number(k), List(v)]);
  return ImmutableMap(entries);
}

function maybeAddDepartmentUsage(
  usages: Record<string, FilterWordProfileUsageRecord[]>,
  department: DepartmentWithTeams
): void {
  if (department.filterWordProfileId) {
    const current = usages[department.filterWordProfileId] || [];
    current.push({
      department,
    });
    // IMPORTANT: must set this BEFORE the team calls to avoid losing data.
    usages[department.filterWordProfileId] = current;
  }
  department.teams.forEach(team => maybeAddTeamUsage(usages, department, team));
}

function maybeAddTeamUsage(
  usages: Record<string, FilterWordProfileUsageRecord[]>,
  department: DepartmentWithTeams,
  team: Team
): void {
  if (!team.filterWordProfileId) {
    return;
  }
  const current = usages[team.filterWordProfileId] || [];
  current.push({
    department,
    team,
  });
  usages[team.filterWordProfileId] = current;
}

export default alt.createStore(AdminFilterWordProfileStore);
